import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { FileText, Download, Trash2, Loader2, Check, Upload } from "lucide-react";

const ZONES = ["N1", "N2", "E", "NE", "W1", "W2", "S1", "S2", "Central"];
const RC_STATUS_COLORS = {
  draft: "bg-amber-100 text-amber-700",
  active: "bg-green-100 text-green-700",
  superseded: "bg-slate-100 text-slate-500",
  discarded: "bg-rose-100 text-rose-600",
};
const OVERHEAD_FIELDS = [
  ["fsc_percent", "FSC %"], ["min_charge", "Min LR charge ₹"], ["min_charged_wt", "Min charged wt (kg)"],
  ["volumetric_divisor", "Divisor"], ["docket_charge", "Processing ₹/LR"], ["gst_percent", "GST %"],
  ["oda_per_kg", "ODA ₹/kg"], ["oda_min", "ODA min ₹"],
  ["fm_per_kg", "FM ₹/kg"], ["lm_per_kg", "LM ₹/kg"],
  ["green_tax_per_kg", "Green ₹/kg"], ["green_tax_min", "Green min ₹"],
  ["appointment_per_kg", "Appt ₹/kg"], ["appointment_min", "Appt min ₹"],
  ["demurrage_per_kg", "Demurrage ₹/kg"], ["demurrage_min", "Demurrage min ₹"],
  ["free_storage_days", "Free storage days"], ["floor_delivery_per_kg", "Floor delivery ₹/kg"],
];

/**
 * Admin per-seller rate-card manager: upload a seller's signed ABD agreement (versioned by
 * effective date), review the Gemini-extracted SELL rate card, and confirm it to active.
 * Mirrors the Delhivery partner rate-card flow but scoped to one seller; kept as its own
 * component so it never touches the live B2B Shipping Partner page.
 */
export default function SellerRateCardManager({ seller, open, onOpenChange }) {
  const sellerId = seller?.seller_id;
  const [agreements, setAgreements] = useState([]);
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ title: "", effective_from: "", signed_date: "", note: "", file: null });
  const [selected, setSelected] = useState(null);  // editable local copy of a card under review

  const load = useCallback(async () => {
    if (!sellerId) return;
    setLoading(true);
    try {
      const [ag, rc] = await Promise.all([
        axios.get(`${API}/seller-rate-cards/${sellerId}/agreements`),
        axios.get(`${API}/seller-rate-cards`, { params: { seller_id: sellerId } }),
      ]);
      setAgreements(ag.data.agreements || []);
      setCards(rc.data.cards || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load seller rate cards");
    } finally {
      setLoading(false);
    }
  }, [sellerId]);

  useEffect(() => { if (open) { setSelected(null); load(); } }, [open, load]);

  const upload = async (e) => {
    e.preventDefault();
    if (!form.file) { toast.error("Choose a PDF"); return; }
    if (!form.title.trim() || !form.effective_from) { toast.error("Title and effective date are required"); return; }
    setUploading(true);
    const fd = new FormData();
    fd.append("file", form.file);
    fd.append("title", form.title.trim());
    fd.append("effective_from", form.effective_from);
    if (form.signed_date) fd.append("signed_date", form.signed_date);
    if (form.note.trim()) fd.append("note", form.note.trim());
    try {
      await axios.post(`${API}/seller-rate-cards/${sellerId}/agreements`, fd);
      toast.success("Agreement uploaded — extracting the rate card in the background…");
      setForm({ title: "", effective_from: "", signed_date: "", note: "", file: null });
      // extraction runs async; refresh now and again shortly so the draft appears
      load();
      setTimeout(load, 6000);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const voidAgreement = async (id) => {
    const reason = window.prompt("Reason for voiding this agreement?");
    if (!reason) return;
    const fd = new FormData();
    fd.append("reason", reason);
    try {
      await axios.post(`${API}/seller-rate-cards/${sellerId}/agreements/${id}/void`, fd);
      toast.success("Agreement voided");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to void");
    }
  };

  const review = (card) => setSelected(JSON.parse(JSON.stringify(card)));
  const setCell = (o, d, v) => setSelected((c) => {
    const zm = { ...(c.zone_matrix || {}) }; zm[o] = { ...(zm[o] || {}) };
    zm[o][d] = v === "" ? null : parseFloat(v);
    return { ...c, zone_matrix: zm };
  });
  const setOverhead = (k, v) => setSelected((c) => ({ ...c, overheads: { ...(c.overheads || {}), [k]: v === "" ? null : parseFloat(v) } }));

  const saveDraft = async () => {
    setBusy(true);
    try {
      const body = {
        title: selected.title, effective_from: selected.effective_from,
        zone_matrix: selected.zone_matrix, overheads: selected.overheads,
        location_flat_charges: selected.location_flat_charges,
      };
      const res = await axios.put(`${API}/seller-rate-cards/${selected.id}`, body);
      toast.success("Draft saved");
      setSelected(JSON.parse(JSON.stringify(res.data)));
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    } finally { setBusy(false); }
  };

  const confirmCard = async (card) => {
    if (!window.confirm(`Activate this rate card (effective ${card.effective_from})? It becomes the seller's live B2B rate card and supersedes the current active one.`)) return;
    setBusy(true);
    try {
      await axios.post(`${API}/seller-rate-cards/${card.id}/confirm`);
      toast.success("Rate card activated");
      setSelected(null); load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to confirm");
    } finally { setBusy(false); }
  };

  const discardCard = async (card) => {
    if (!window.confirm("Discard this draft? It stays in the log, marked discarded.")) return;
    setBusy(true);
    try {
      await axios.post(`${API}/seller-rate-cards/${card.id}/discard`);
      toast.success("Draft discarded");
      setSelected(null); load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to discard");
    } finally { setBusy(false); }
  };

  const isDraft = selected?.status === "draft";
  const nullCells = selected ? ZONES.reduce((n, o) => n + ZONES.filter((d) => selected.zone_matrix?.[o]?.[d] == null).length, 0) : 0;
  const conf = selected?.parse_confidence;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>B2B Rate Card — {seller?.brand_name || sellerId}</DialogTitle>
          <DialogDescription>
            Upload the seller's signed agreement; the SELL rate card is extracted automatically. Review and confirm it to make it the seller's live card.
          </DialogDescription>
        </DialogHeader>

        {!selected ? (
          <div className="space-y-5">
            {/* Upload */}
            <form onSubmit={upload} className="rounded-lg border p-3 space-y-3 bg-slate-50/50">
              <div className="text-sm font-medium">Upload a new agreement</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><Label className="text-xs">Title *</Label>
                  <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="ABD Agreement — Rate CFT" /></div>
                <div><Label className="text-xs">Effective from *</Label>
                  <Input type="date" value={form.effective_from} onChange={(e) => setForm({ ...form, effective_from: e.target.value })} /></div>
                <div><Label className="text-xs">Signed date</Label>
                  <Input type="date" value={form.signed_date} onChange={(e) => setForm({ ...form, signed_date: e.target.value })} /></div>
                <div><Label className="text-xs">PDF file *</Label>
                  <Input type="file" accept="application/pdf" onChange={(e) => setForm({ ...form, file: e.target.files?.[0] || null })} /></div>
              </div>
              <div><Label className="text-xs">Note</Label>
                <Textarea rows={1} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} /></div>
              <Button type="submit" disabled={uploading} style={{ backgroundColor: "#00A699" }} className="hover:opacity-90">
                {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />} Upload & extract
              </Button>
            </form>

            {/* Rate card versions */}
            <div>
              <div className="text-sm font-medium mb-1">Rate card versions</div>
              {loading ? (
                <div className="flex items-center gap-2 text-sm text-slate-500 py-4"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
              ) : cards.length === 0 ? (
                <p className="text-sm text-slate-400 py-3">No rate cards yet — upload an agreement to extract one.</p>
              ) : (
                <div className="space-y-1">
                  {cards.map((c) => (
                    <button key={c.id} onClick={() => review(c)}
                      className="w-full flex items-center justify-between gap-2 rounded-lg border px-3 py-2 text-left hover:bg-slate-50">
                      <div>
                        <div className="text-sm font-medium">{c.title}</div>
                        <div className="text-xs text-slate-500">effective {c.effective_from}
                          {c.parse_confidence != null && <span> · {Math.round(c.parse_confidence * 100)}% parsed</span>}</div>
                      </div>
                      <Badge className={RC_STATUS_COLORS[c.status]}>{c.status}</Badge>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Agreements log */}
            <div>
              <div className="text-sm font-medium mb-1">Agreement log</div>
              {agreements.length === 0 ? (
                <p className="text-sm text-slate-400 py-2">No agreements uploaded yet.</p>
              ) : (
                <div className="space-y-1">
                  {agreements.map((a) => (
                    <div key={a.id} className={`flex items-center justify-between gap-2 rounded-lg border px-3 py-2 ${a.voided_at ? "opacity-50" : ""}`}>
                      <div className="flex items-center gap-2 min-w-0">
                        <FileText className="w-4 h-4 text-slate-400 shrink-0" />
                        <div className="min-w-0">
                          <div className="text-sm truncate">{a.title} {a.voided_at && <span className="text-rose-500">(voided)</span>}</div>
                          <div className="text-xs text-slate-500">effective {a.effective_from}{a.file_name ? ` · ${a.file_name}` : ""}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        {a.download_url && (
                          <a href={a.download_url} target="_blank" rel="noopener noreferrer"
                             className="p-1.5 rounded hover:bg-slate-100 text-slate-500" title="Download"><Download className="w-4 h-4" /></a>
                        )}
                        {!a.voided_at && (
                          <button onClick={() => voidAgreement(a.id)} className="p-1.5 rounded hover:bg-rose-50 text-rose-500" title="Void"><Trash2 className="w-4 h-4" /></button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        ) : (
          /* ── review panel ── */
          <div className="space-y-4">
            <div className="flex items-center justify-between gap-2">
              <button className="text-sm text-[#00A699] hover:underline" onClick={() => setSelected(null)}>← All versions</button>
              <Badge className={RC_STATUS_COLORS[selected.status]}>{selected.status}</Badge>
            </div>

            <div className={`rounded-lg p-3 text-sm ${conf == null ? "bg-slate-50" : conf >= 0.9 ? "bg-green-50 text-green-800" : conf >= 0.7 ? "bg-amber-50 text-amber-800" : "bg-red-50 text-red-800"}`}>
              Extraction confidence: <b>{conf != null ? `${Math.round(conf * 100)}%` : "n/a"}</b>
              {nullCells > 0 && <span> · {nullCells} matrix cell(s) couldn’t be read — fill the red cells before confirming.</span>}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><Label className="text-xs">Title</Label>
                <Input value={selected.title || ""} disabled={!isDraft} onChange={(e) => setSelected((c) => ({ ...c, title: e.target.value }))} /></div>
              <div><Label className="text-xs">Effective from</Label>
                <Input type="date" value={selected.effective_from || ""} disabled={!isDraft} onChange={(e) => setSelected((c) => ({ ...c, effective_from: e.target.value }))} /></div>
            </div>

            <div>
              <div className="text-sm font-medium mb-1">SELL zone matrix — ₹/kg (origin ↓ × destination →)</div>
              <div className="overflow-x-auto border rounded-lg">
                <table className="text-xs border-collapse">
                  <thead><tr><th className="p-1 sticky left-0 bg-white"></th>
                    {ZONES.map((d) => <th key={d} className="p-1 font-semibold text-slate-500">{d}</th>)}</tr></thead>
                  <tbody>
                    {ZONES.map((o) => (
                      <tr key={o}>
                        <td className="p-1 font-semibold text-slate-500 sticky left-0 bg-white">{o}</td>
                        {ZONES.map((d) => {
                          const val = selected.zone_matrix?.[o]?.[d];
                          return (
                            <td key={d} className="p-0.5">
                              <input type="number" step="0.1" value={val ?? ""} disabled={!isDraft}
                                onChange={(e) => setCell(o, d, e.target.value)}
                                className={`w-14 px-1 py-0.5 text-right rounded border ${val == null ? "border-red-300 bg-red-50" : "border-slate-200"} ${isDraft ? "" : "bg-slate-50 text-slate-500"}`} />
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div>
              <div className="text-sm font-medium mb-1">Overheads</div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {OVERHEAD_FIELDS.map(([key, label]) => (
                  <div key={key}>
                    <Label className="text-[11px] text-slate-500">{label}</Label>
                    <Input type="number" step="0.01" value={selected.overheads?.[key] ?? ""} disabled={!isDraft}
                      onChange={(e) => setOverhead(key, e.target.value)} className="h-8" />
                  </div>
                ))}
              </div>
            </div>

            {Array.isArray(selected.location_flat_charges) && selected.location_flat_charges.length > 0 && (
              <div>
                <div className="text-sm font-medium mb-1">Flat location charges</div>
                <div className="flex flex-wrap gap-2 text-xs">
                  {selected.location_flat_charges.map((l, i) => (
                    <span key={i} className="rounded border px-2 py-1 bg-slate-50">{l.locations}: ₹{l.amount}/{l.per || "GR"}</span>
                  ))}
                </div>
              </div>
            )}

            {Array.isArray(selected.extra?.lane_surcharges) && selected.extra.lane_surcharges.length > 0 && (
              <div>
                <div className="text-sm font-medium mb-1">Additive lane surcharges</div>
                <div className="flex flex-wrap gap-2 text-xs">
                  {selected.extra.lane_surcharges.map((s, i) => (
                    <span key={i} className="rounded border px-2 py-1 bg-slate-50">{s.match}: +₹{s.add_per_kg}/kg</span>
                  ))}
                </div>
              </div>
            )}

            {Array.isArray(selected.extra?.extra_charges) && selected.extra.extra_charges.length > 0 && (
              <div>
                <div className="text-sm font-medium mb-1">Other agreement charges</div>
                <div className="flex flex-wrap gap-2 text-xs">
                  {selected.extra.extra_charges.map((c, i) => (
                    <span key={i} className="rounded border px-2 py-1 bg-slate-50">{c.name}{c.rate != null ? `: ₹${c.rate}${c.per ? "/" + c.per : ""}` : ""}</span>
                  ))}
                </div>
              </div>
            )}

            {isDraft && (
              <div className="flex flex-wrap gap-2 pt-2 border-t">
                <Button variant="outline" onClick={saveDraft} disabled={busy}>Save edits</Button>
                <Button onClick={() => confirmCard(selected)} disabled={busy || nullCells > 0} style={{ backgroundColor: "#00A699" }} className="hover:opacity-90">
                  {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />} Confirm & activate
                </Button>
                <Button variant="outline" onClick={() => discardCard(selected)} disabled={busy} className="text-rose-600 border-rose-200 hover:bg-rose-50">Discard draft</Button>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
